import { importCustomersMapThunk, getMapThunk } from '@redux/features/map.slice';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import Modal from 'Common/Components/Modal';
import ButtonBase from 'components/ui/buttons/ButtonBase';
import { useFormik } from 'formik';
import React, { FC } from 'react';
import * as Yup from 'yup';

type IProps = {
    show: boolean;
    handleClose: () => void;
};

const ImportCustomersModal: FC<IProps> = ({ show, handleClose }) => {
    const { loading } = useAppSelector((state: RootState) => state.mapState);
    const dispatch = useAppDispatch();

    const validationSchema = Yup.object({
        file: Yup.mixed()
            .required('File is required')
            .test('fileType', 'Only CSV and Excel files are allowed', (value: any) => {
                if (!value) return false;
                const allowedTypes = [
                    'text/csv',
                    'application/vnd.ms-excel',
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                ];
                return (
                    allowedTypes.includes(value.type) ||
                    value.name?.endsWith('.csv') ||
                    value.name?.endsWith('.xlsx') ||
                    value.name?.endsWith('.xls')
                );
            }),
    });

    const handleSubmit = (values: any) => {
        const formData = new FormData();
        formData.append('file', values.file);

        dispatch(importCustomersMapThunk(formData)).then((res: any) => {
            if (res.meta.requestStatus === 'fulfilled') {
                dispatch(getMapThunk());
                handleClose();
                formik.resetForm();
            }
        });
    };

    const formik = useFormik({
        initialValues: {
            file: undefined as unknown as File,
        },
        validationSchema,
        onSubmit: handleSubmit,
    });

    return (
        <Modal
            show={show}
            onHide={handleClose}
            dialogClassName="w-full bg-white p-6 shadow-xl rounded-lg border border-gray-200 overflow-scroll md:w-[550px] md:max-h-[85vh]"
            backdropClassName="backdrop-overlay fixed inset-0 z-[1051] bg-black/70 backdrop-blur-sm"
        >
            <Modal.Header className="border-b border-gray-200 pb-4 mb-4 flex justify-between items-center">
                <Modal.Title className="text-xl font-semibold text-gray-800">Import Customers Map</Modal.Title>
            </Modal.Header>
            <Modal.Body className="my-4">
                <form onSubmit={formik.handleSubmit} className="flex flex-col gap-5">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">
                            Select File (CSV or Excel)
                        </label>
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-primary transition-colors">
                            <input
                                type="file"
                                accept=".csv,.xlsx,.xls"
                                onChange={(e) => {
                                    const file = e.currentTarget.files?.[0];
                                    if (file) {
                                        formik.setFieldValue('file', file);
                                    }
                                }}
                                onBlur={formik.handleBlur}
                                className="block w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-white hover:file:bg-primary-dark cursor-pointer"
                            />
                        </div>
                        {formik.touched.file && formik.errors.file && (
                            <p className="mt-2 text-sm text-red-600 font-medium">{String(formik.errors.file)}</p>
                        )}
                        {formik.values.file && (
                            <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-md">
                                <p className="text-sm text-green-800 font-medium">
                                    ✓ Selected: <span className="font-semibold">{formik.values.file.name}</span>
                                </p>
                            </div>
                        )}
                    </div>
                </form>
            </Modal.Body>
            <Modal.Footer className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
                <ButtonBase
                    innerText="Cancel"
                    className="bg-gray-500 hover:bg-gray-600 rounded border-none px-4 py-2 text-white"
                    onClick={() => {
                        handleClose();
                        formik.resetForm();
                    }}
                    disabled={loading}
                />
                <ButtonBase
                    innerText="Import Customers"
                    className="bg-primary hover:bg-primary-dark rounded border-none px-4 py-2 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={formik.handleSubmit}
                    disabled={loading || !formik.values.file}
                />
            </Modal.Footer>
        </Modal>
    );
};

export default ImportCustomersModal;
